<template>
    <div>
        <breadcrumbs class="mb-3" />
        <page-title :text="$t('page_titles.company_agencies_page_title')" />
        <div class="mb-4 d-flex align-center flex-wrap">
            <v-btn
                data-cy="create"
                variant="flat"
                :to="{name: 'company-client-profile', params: {id: 0}}"
                class="bg-primary me-2 mb-2"
            >
                {{ $t('new_agency_client_profile') }}
            </v-btn>
            <v-btn
                class="me-2 mb-2"
                variant="outlined"
                color="primary"
                :disabled="!selectedCompanies.length"
                data-cy="show-edit-companies-modal"
                @click="isEditCompaniesModal = true"
            >
                {{ $t('edit_clients_profiles') }}
            </v-btn>
            <v-btn
                v-if="isTO1"
                data-cy="send-email"
                variant="outlined"
                color="primary"
                class="me-2 mb-2"
                :disabled="!emails.length"
                @click="emailModal = true"
            >
                {{ $t('send_email') }}
            </v-btn>
            <file-download-modal
                v-if="isTO1"
                v-model="isShowDownloadExcelMenu"
                data-cy="download-file"
                :radio-items="downloadRadioItems"
                :label="$t('agencies_download')"
                :loading="isLoadingDownloadExcel"
                @change-radio-group="radioGroupDownloadExcel = $event"
                @download="downloadFile"
            />
            <file-upload-modal
                v-if="isTO1"
                v-model="isShowUploadExcelMenu"
                data-cy="upload-excel"
                class="ms-4"
                :label="$t('agencies_upload')"
                :sample-link="sampleLink"
                @upload="uploadExcel"
            />
        </div>
        <companies-table
            ref="table"
            :company-type="['TOUR_AGENCY', 'TOUR_OPERATOR']"
            @change-selected-emails="emails = $event"
            @change-selected-companies="selectedCompanies = $event"
        />
        <EditCompaniesModal v-model="isEditCompaniesModal" :companies="selectedCompanies" @sent="updateData" />
        <send-clients-email-modal v-model="emailModal" :emails="emails" @sent="$refs.table.selected = []" />
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import CompaniesTable from '@/components/account/CompaniesTable.vue'
    import SendClientsEmailModal from '@/components/account/modals/SendClientsEmailModal.vue'
    import FileUploadModal from '~src/components/account/snippets/FileUploadModal.vue'
    import FileDownloadModal from '~src/components/account/snippets/FileDownloadModal.vue'
    import {fullUrl} from '~/utils/host-adapter'
    import {downloadData} from '~/utils/helpers'
    import EditCompaniesModal from '~/components/account/companies/EditCompaniesModal.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'

    @Component({
        components: {
            FileUploadModal,
            FileDownloadModal,
            SendClientsEmailModal,
            Breadcrumbs,
            CompaniesTable,
            EditCompaniesModal,
            PageTitle,
        },
    })
    export class CompanyAgenciesPage extends Vue {
        emails: string[] = []
        emailModal: boolean = false
        selectedCompanies: number[] = []
        isEditCompaniesModal: boolean = false

        isShowDownloadExcelMenu: boolean = false
        isLoadingDownloadExcel: boolean = false
        radioGroupDownloadExcel: string = ''

        isShowUploadExcelMenu: boolean = false
        isLoadingUploadExcel: boolean = false

        updateData(): void {
            this.$refs.table.selected = []
            this.$refs.table.loadCompanies()
        }

        async uploadExcel(file) {
            this.isLoadingUploadExcel = true
            const formData = new FormData()
            formData.append('file', file)
            try {
                await this.$api.importOrganisations.post(formData)
                this.isShowUploadExcelMenu = false
                this.updateData()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isLoadingUploadExcel = false
            }
        }

        async downloadFile(format) {
            this.isLoadingDownloadExcel = true

            try {
                let data

                if (this.radioGroupDownloadExcel === 'selected') {
                    data = await this.$api.downloadCompanies.get({
                        companyIds: this.selectedCompanies,
                        companyType: 'ORGANIZATION',
                        fileType: format.fileType,
                    })
                } else {
                    data = await this.$api.downloadCompanies.get({
                        filterKey: runtimeStore.companiesFilterKey,
                        companyType: 'ORGANIZATION',
                        fileType: format.fileType,
                    })
                }

                await downloadData(data, 'agencies.xlsx')
                this.isShowDownloadExcelMenu = false
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isLoadingDownloadExcel = false
            }
        }

        get sampleLink() {
            return fullUrl('/stat/files/CompaniesUsersImport.xlsx')
        }

        get isTO1() {
            return authStore.isTO1
        }

        get downloadRadioItems() {
            return [
                {
                    value: 'selected',
                    label: `${this.$t('selected_agencies')} ${this.selectedCompanies?.length || 0}`,
                    disabled: !this.selectedCompanies?.length,
                },
                {value: 'all', label: this.$t('all_agencies')},
            ]
        }
    }

    export default CompanyAgenciesPage
</script>
